import type { Maybe } from '@adornis/base/utilTypes.js';
import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { scopedCollection } from '../server/scoped-collection.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-util/middlewares/checkAuthorizedMiddleware.js';
import type { ServerCachedUser } from '@adornis/bh-util/util.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { map } from 'rxjs/operators';
import { getDbPluginForScope } from '../db-plugins.js';
import { UserStammdaten } from '../entities/stammdaten.js';
import { Permission } from '../enums/roles.js';
import { mongo } from '../server/mongo.js';
import type { BHTranslationDictionary } from '../translations/translation.js';
import { getAllowedUsers } from './users.js';

export const saveOrCreateStammdaten = registerQuery({
  operationName: 'saveOrCreateStammdaten',
  params: [{ name: 'stammdaten', type: () => UserStammdaten }],
  type: () => UserStammdaten,
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) => {
      if (user._id === params.stammdaten.userID) return true;
      return user.hasPermission(Permission.EDIT_ADMIN_INSTITUTION_USERS, params.stammdaten.institutionID);
    }),
  ],
  resolve: (stammdaten: UserStammdaten) => async (gqlFields: BaseQLSelectionSet<UserStammdaten>) => {
    const stammdatenColleciton = await scopedCollection<UserStammdaten>(UserStammdaten._class);
    const existingStammdaten = await stammdatenColleciton.findOne({
      userID: stammdaten.userID,
      institutionID: stammdaten.institutionID,
    });

    if (existingStammdaten) {
      stammdaten._id = existingStammdaten._id;
      await stammdaten.save();
    } else await stammdaten.create();

    return stammdaten;
  },
});

export const getStammdatenByUserAndInstitution = registerQuery({
  operationName: 'getStammdatenByUserAndInstitution',
  params: [
    { name: 'userID', type: () => String },
    { name: 'institutionID', type: () => String },
  ],
  type: () => UserStammdaten,
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) => {
      if (user._id === params.userID) return true;
      if (user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_USERS, params.institutionID)) return true;
      return false;
    }),
  ],
  resolve:
    <T extends UserStammdaten>(userID: string, institutionID: string) =>
    async <Tsel extends BaseQLSelectionSet<T>>(gqlFields: Tsel) => {
      const collection = mongo().collection<EntityData<UserStammdaten>>(UserStammdaten._collectionName);
      const result = await collection.findOne({ userID, institutionID });
      return result as NarrowedEntity<T, Tsel> | null;
    },
});

export function createStammdatenForInstitutionID(institutionID: string | null, createInDB: boolean = false) {
  const stammdatenPlugin = getDbPluginForScope('registration::stammdaten', institutionID ?? 'default');
  return stammdatenPlugin.createStammdaten({ institutionID, createInDB });
}

export const getExistingStammdaten = registerQuery({
  operationName: 'getExistingStammdaten',
  type: () => UserStammdaten,
  params: [
    { name: 'userID', type: () => String },
    { name: 'institutionID', type: () => String },
    { name: 'id', type: () => String },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) => {
      if (user._id === params.userID) return true;
      return user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_USERS, params.institutionID);
    }),
  ],
  resolve: <TStammdaten>(userID, institutionID, id) => {
    return async (gqlFields: BaseQLSelectionSet<TStammdaten>) => {
      const collection = await scopedCollection<UserStammdaten>(UserStammdaten._class);
      const existing = await collection.findOne<UserStammdaten>({
        $or: [
          { _id: id, userID, institutionID },
          { userID, institutionID },
        ],
      });
      return existing as Maybe<TStammdaten>;
    };
  },
});

export const subscribeByUserAndInstitution = registerSubscription({
  operationName: 'subscribeByUserAndInstitution',
  type: () => UserStammdaten,
  params: [
    { name: 'userID', type: () => String },
    { name: 'institutionID', type: () => String },
  ],
  middlewares: [
    checkAuthorizedMiddleware(({ user, params }) => {
      if (user._id === params.userID) return true;
      return user.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_USERS, params.institutionID);
    }),
  ],
  resolve: (userID, institutionID) => {
    return (gqlFields: BaseQLSelectionSet<UserStammdaten>) => {
      return watchQuery<UserStammdaten>(UserStammdaten._collectionName, { userID, institutionID }).pipe(
        map(result => result[0]),
      );
    };
  },
});

export async function getAllowedUserStammdaten(user: ServerCachedUser) {
  const userIDs = await getAllowedUsers(user);
  const aggregation = [{ $match: { userID: { $in: userIDs } } }, { $group: { _id: null, ids: { $push: '$_id' } } }];

  const userStammdatenCollection = mongo().collection(UserStammdaten._collectionName);
  const result = await userStammdatenCollection.aggregate(aggregation).toArray();

  return result.at(0)?.ids ?? [];
}

export function getStammdatenFieldsForInstitution(institutionID: string) {
  const stammdatenPlugin = getDbPluginForScope('registration::stammdaten', institutionID);
  const fields = (() => {
    const stammdatenClass = stammdatenPlugin.classType();
    // add expception for base class to not get all fields of all inheriting classes
    if (stammdatenClass._class === 'UserStammdaten') return ['userID', 'institutionID', 'institution'];
    return Object.keys(stammdatenClass.allFields);
  })();

  return fields;
}

export function getEnumTranslation(
  translationController: TranslationController<BHTranslationDictionary>,
  value: string,
) {
  if (value.startsWith('ICSRM_') || value.startsWith('SD_') || value.startsWith('TUDA_') || value.startsWith('PBS_')) {
    return translationController.translate('enums', value as any);
  }
}
